import { FC, useMemo } from "react";
import { Button } from "@/components/button";
import { ColumnDef } from "@tanstack/react-table";
import { CapturedWebsiteSummarySchema } from "@/domains/openapi/types";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { orderBy } from "lodash-es";
import { DateTime } from "luxon";

interface CapturedWebsitesTableProps {
  data: CapturedWebsiteSummarySchema[];
  onViewCapturedWebsite: (id: string) => void;
  pageSize: number;
}

export const CapturedWebsitesTable: FC<CapturedWebsitesTableProps> = ({
  data,
  onViewCapturedWebsite,
  pageSize,
}) => {
  const columns = useMemo<ColumnDef<CapturedWebsiteSummarySchema>[]>(
    () => [
      { header: "ID", cell: ({ row }) => <UuidFieldRow id={row.original.id} /> },
      {
        header: "Title",
        cell: ({ row }) => (
          <div
            style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.original.website_title ?? "Untitled"}
          </div>
        ),
      },
      {
        header: "URL",
        cell: ({ row }) => (
          <div
            style={{
              maxWidth: "250px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.original.website_url ?? "No URL"}
          </div>
        ),
      },
      {
        header: "Created At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.created_at} />,
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button size="1" onClick={() => onViewCapturedWebsite(row.original.id)}>
            View
          </Button>
        ),
      },
    ],
    [onViewCapturedWebsite]
  );

  const sortedData = useMemo(
    () => orderBy(data, x => DateTime.fromISO(x.created_at).toMillis(), "desc"),
    [data]
  );

  return <Table data={sortedData} columns={columns} pageSize={pageSize} />;
};
